import {
  BadRequestException,
  ConflictException,
  ForbiddenException,
  Injectable,
  NotFoundException,
} from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import {
  CreateStorefrontTemplateRequest,
  UpdateStorefrontTemplateRequest,
} from '@cart/shared-types';
import { StoreConfigService } from '../tenancy/store-config.service';
import { STOREFRONT_TEMPLATE_SELECT } from './storefront-template.select';
import {
  STOREFRONT_TEMPLATE_TIERS,
  effectivePlan,
  isStorefrontTemplateTier,
  planCoversTier,
} from './storefront-template-tier.util';

const PLATFORM_ROLES = ['admin', 'super_admin', 'platform_admin'];

@Injectable()
export class StorefrontTemplatesService {
  constructor(
    private readonly prisma: PrismaService,
    private readonly storeConfig: StoreConfigService,
  ) {}

  // ── Platform: list all templates (published + draft) ──────────────────────

  async listAll() {
    const items = await this.prisma.storefrontTemplate.findMany({
      where: { isActive: true, deletedAt: null },
      select: STOREFRONT_TEMPLATE_SELECT,
      orderBy: [{ isPublished: 'desc' }, { sortOrder: 'asc' }, { createdAt: 'desc' }],
    });
    return { items, total: items.length };
  }

  // ── Merchant: list published templates, annotated with entitlement ────────

  /**
   * Returns EVERY published template, including the ones this organisation
   * cannot use, each carrying `isLocked`. Hiding locked templates would make
   * the picker useless as an upsell surface; the real gate is `applyToStore`.
   */
  async listForMerchant(actorId: string, storeId?: string) {
    const items = await this.prisma.storefrontTemplate.findMany({
      where: { isPublished: true, isActive: true, deletedAt: null },
      select: STOREFRONT_TEMPLATE_SELECT,
      orderBy: [{ sortOrder: 'asc' }, { name: 'asc' }],
    });

    const plan = await this.resolveActorPlan(actorId, storeId);
    const annotated = items.map((item) => ({
      ...item,
      isLocked: !planCoversTier(plan, item.tier),
    }));

    return { items: annotated, total: annotated.length };
  }

  // ── Platform: get single template ─────────────────────────────────────────

  async getById(id: string) {
    const template = await this.prisma.storefrontTemplate.findFirst({
      where: { id, isActive: true, deletedAt: null },
      select: STOREFRONT_TEMPLATE_SELECT,
    });
    if (!template) throw new NotFoundException(`Storefront template ${id} not found`);
    return template;
  }

  // ── Platform: create ──────────────────────────────────────────────────────

  async create(dto: CreateStorefrontTemplateRequest, actorId: string) {
    const slug = this.slugify(dto.name);
    const existing = await this.prisma.storefrontTemplate.findUnique({ where: { slug } });
    if (existing) {
      throw new ConflictException(`Storefront template slug "${slug}" already exists`);
    }

    const tier = this.validateTier(dto.tier) ?? 'free';

    return this.prisma.storefrontTemplate.create({
      data: {
        name: dto.name,
        slug,
        description: dto.description ?? null,
        previewImageUrl: dto.previewImageUrl ?? null,
        tier,
        // `undefined`, not `null`: Prisma 5 rejects a literal null on a
        // nullable Json field (it wants Prisma.DbNull/JsonNull to disambiguate
        // SQL NULL from JSON null). Leaving it unset lets the column default
        // to NULL, which is the same outcome without the special value.
        metadata: (dto.metadata ?? undefined) as object | undefined,
        // New templates are always drafts. Publishing is what exposes a slug
        // to real storefronts, and it must not happen as a side effect of
        // creation — the code registry in web/ has to exist first.
        isPublished: false,
        isActive: true,
        createdBy: actorId,
        updatedBy: actorId,
      },
      select: STOREFRONT_TEMPLATE_SELECT,
    });
  }

  // ── Platform: update ──────────────────────────────────────────────────────

  async update(id: string, dto: UpdateStorefrontTemplateRequest, actorId: string) {
    await this.getById(id);

    const updates: Record<string, unknown> = { updatedBy: actorId };
    if (dto.name !== undefined) {
      updates['name'] = dto.name;
      updates['slug'] = this.slugify(dto.name);
    }
    if (dto.description !== undefined) updates['description'] = dto.description;
    if (dto.previewImageUrl !== undefined) updates['previewImageUrl'] = dto.previewImageUrl;
    if (dto.tier !== undefined) updates['tier'] = this.validateTier(dto.tier);
    // Replaced wholesale rather than merged: `metadata` is a display blob for
    // the picker, so a caller clearing a feature bullet must be able to.
    // `!= null` also swallows an explicit JSON null in the request body, which
    // Prisma 5 would otherwise reject on a nullable Json column — send `{}` to
    // empty it out.
    if (dto.metadata != null) updates['metadata'] = dto.metadata;

    return this.prisma.storefrontTemplate.update({
      where: { id },
      data: updates,
      select: STOREFRONT_TEMPLATE_SELECT,
    });
  }

  // ── Platform: publish / unpublish ────────────────────────────────────────

  /**
   * Unpublishing is the global kill switch: `/store/config` refuses to serve
   * an unpublished template and hands back `classic` instead, so every store
   * on a broken template rolls back on its next request with no data change.
   * That is why the whole config cache is dropped here rather than a single
   * store's entry — the blast radius is every tenant.
   */
  async setPublished(id: string, published: boolean, actorId: string) {
    await this.getById(id);
    await this.prisma.storefrontTemplate.update({
      where: { id },
      data: { isPublished: published, updatedBy: actorId },
    });
    this.storeConfig.invalidateAll();
    return { success: true, isPublished: published };
  }

  // ── Platform: soft delete ─────────────────────────────────────────────────

  async remove(id: string, actorId: string) {
    await this.getById(id);

    // Detach before deleting so affected stores land on NULL — which resolves
    // to `classic` — rather than pointing at a tombstoned row.
    await this.prisma.store.updateMany({
      where: { storefrontTemplateId: id },
      data: { storefrontTemplateId: null, updatedBy: actorId },
    });

    await this.prisma.storefrontTemplate.update({
      where: { id },
      data: { isActive: false, deletedAt: new Date(), deletedBy: actorId, updatedBy: actorId },
    });
    this.storeConfig.invalidateAll();
    return { success: true };
  }

  // ── Merchant: apply template to store ─────────────────────────────────────

  /**
   * The authoritative gate. The `isLocked` annotation on the list and any
   * disabled button in admin are UX; this method is what actually decides.
   */
  async applyToStore(storeId: string, templateId: string, actorId: string, actorRole: string) {
    const template = await this.prisma.storefrontTemplate.findFirst({
      where: { id: templateId, isPublished: true, isActive: true, deletedAt: null },
      select: { id: true, tier: true },
    });
    if (!template) throw new NotFoundException('Storefront template not found or not published');

    // Platform roles can apply to any store; merchants must own the store.
    if (!PLATFORM_ROLES.includes(actorRole)) {
      const staff = await this.prisma.storeStaff.findFirst({
        where: { storeId, userId: actorId, isActive: true, deletedAt: null },
        select: { id: true },
      });
      if (!staff) throw new ForbiddenException('You do not have access to this store');
    }

    // Tier gate — `plan` lives on Organization, never on Store, so this is a
    // join. Enforced for platform roles too: applying a premium template to a
    // free org would produce a storefront the merchant is not paying for and
    // an entitlementLapsed banner they cannot clear.
    const store = await this.prisma.store.findUnique({
      where: { id: storeId },
      select: { organization: { select: { plan: true, planExpiresAt: true } } },
    });
    if (!store) throw new NotFoundException('Store not found');

    const plan = effectivePlan(store.organization?.plan, store.organization?.planExpiresAt);
    if (!planCoversTier(plan, template.tier)) {
      throw new ForbiddenException(
        `This storefront template requires the "${template.tier}" plan`,
      );
    }

    await this.prisma.store.update({
      where: { id: storeId },
      data: { storefrontTemplateId: templateId, updatedBy: actorId },
    });
    this.storeConfig.invalidate(storeId);
    return { success: true, storefrontTemplateId: templateId };
  }

  // ── Merchant: get their store's current template ──────────────────────────

  async getStoreTemplate(storeId: string) {
    const store = await this.prisma.store.findUnique({
      where: { id: storeId },
      select: { storefrontTemplate: { select: STOREFRONT_TEMPLATE_SELECT } },
    });
    if (!store) throw new NotFoundException('Store not found');
    // null is a legitimate answer, not an error: it means "never picked one",
    // which the storefront renders as `classic`.
    return store.storefrontTemplate ?? null;
  }

  // ── Internals ─────────────────────────────────────────────────────────────

  /**
   * The plan to judge `isLocked` against. Prefers the store the picker is open
   * on; falls back to the actor's own organisation (a merchant with no stores
   * yet has no StoreStaff row — same fallback chain as
   * `MerchantService.resolveOrgId`). Unresolvable ⇒ `free`, which locks the
   * most and unlocks nothing it shouldn't.
   */
  private async resolveActorPlan(actorId: string, storeId?: string): Promise<string> {
    if (storeId) {
      const store = await this.prisma.store.findUnique({
        where: { id: storeId },
        select: { organization: { select: { plan: true, planExpiresAt: true } } },
      });
      if (store?.organization) {
        return effectivePlan(store.organization.plan, store.organization.planExpiresAt);
      }
      return 'free';
    }

    const staff = await this.prisma.storeStaff.findFirst({
      where: { userId: actorId, isActive: true, deletedAt: null },
      select: { organizationId: true },
    });
    const organizationId =
      staff?.organizationId ??
      (
        await this.prisma.user.findUnique({
          where: { id: actorId },
          select: { organizationId: true },
        })
      )?.organizationId;

    if (!organizationId) return 'free';

    const org = await this.prisma.organization.findUnique({
      where: { id: organizationId },
      select: { plan: true, planExpiresAt: true },
    });
    return effectivePlan(org?.plan, org?.planExpiresAt);
  }

  /**
   * `tier` is a plain column guarded by a CHECK constraint, so an unknown
   * value would otherwise surface as a 500 from Postgres rather than a 400.
   */
  private validateTier(tier: string | undefined): string | undefined {
    if (tier === undefined) return undefined;
    if (!isStorefrontTemplateTier(tier)) {
      throw new BadRequestException(
        `tier must be one of: ${STOREFRONT_TEMPLATE_TIERS.join(', ')}`,
      );
    }
    return tier;
  }

  private slugify(name: string): string {
    return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }
}
